import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, X } from "lucide-react";

const SEEN_KEY = "shaktiwings_wishlist_tour_seen_v1";

/**
 * One-time educational tooltip shown the first time a user taps the wishlist (heart) icon.
 *
 * Behaviour:
 *  - Auto-dismisses after 3 seconds.
 *  - Stores "seen" flag in localStorage so it never shows again for this device.
 *  - Anchored to bottom-center of viewport so it does NOT obstruct product image
 *    or the sticky Add-to-Cart bar above it (bottom-28).
 *  - Includes a "View Wishlist" deep-link button + manual close.
 *
 * Triggered via the global `wishlist:tour:show` window event dispatched from ProductCard
 * the first time `useWishlist().toggle()` returns true (added).
 */
export default function WishlistTooltip() {
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    function handler() {
      // Don't show if already seen
      try { if (localStorage.getItem(SEEN_KEY) === "1") return; } catch { /* ignore */ }
      setVisible(true);
      // Auto-dismiss after 3s
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        setVisible(false);
        try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* ignore */ }
      }, 3000);
    }
    window.addEventListener("wishlist:tour:show", handler);
    return () => {
      window.removeEventListener("wishlist:tour:show", handler);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(SEEN_KEY, "1"); } catch { /* ignore */ }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-3 bottom-28 sm:bottom-24 sm:left-auto sm:right-6 sm:w-[340px] z-[100] animate-slide-up"
      role="status"
      aria-live="polite"
      data-testid="wishlist-tour-tooltip"
    >
      <div className="rounded-2xl bg-gradient-to-br from-magenta-600 via-magenta-700 to-amethyst-700 text-white p-3.5 shadow-2xl shadow-magenta-700/40 border border-white/10 flex items-start gap-3">
        <div className="flex-shrink-0 w-9 h-9 rounded-full bg-white/15 backdrop-blur flex items-center justify-center">
          <Heart className="w-4 h-4 fill-white text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold leading-snug">
            💜 Product saved! View all saved items in Wishlist.
          </p>
          <button
            type="button"
            onClick={() => { dismiss(); navigate("/app/wishlist"); }}
            className="mt-2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white text-magenta-700 text-xs font-bold hover:bg-magenta-50 active:scale-95 transition-all"
            data-testid="wishlist-tour-view-btn"
          >
            View Wishlist
          </button>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="flex-shrink-0 -mr-1 -mt-1 w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
          data-testid="wishlist-tour-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
